import { ReactNode, useContext } from 'react';

import { cn } from '@/lib/utils';
import { TopNavBar } from '../Header/TopNavBar';
import { PAGE_CONTAINER } from '../../components/design-system/layout/page-container.constants';
import { AutosaveReporterContext, useAutosaveReporterState } from '../hooks/use-autosave.hook';
import { AutosaveIndicator } from '../../components/design-system/common/AutosaveIndicator';
import { FeatureGate } from '../../components/FeatureGate';
import { PlanFeatures } from '../../modules/superadmin/our-vendors/models/vendor.model';

interface BreadcrumbItemProps {
  label: string;
  /**
   * Accepted and ignored. Breadcrumbs are text now — an icon at this size
   * only added noise. Kept so the pages that still pass one keep compiling;
   * remove it once none do.
   */
  icon?: ReactNode;
  href?: string;
}

interface PageContainerProps {
  children: ReactNode;
  title?: string;
  breadcrumbs?: BreadcrumbItemProps[];
  hideTopNav?: boolean;
  /**
   * Shown at the top right. Without it, the page shows the save status of any
   * form inside it that autosaves (`useAutosave`), and nothing otherwise.
   */
  status?: ReactNode;
  /**
   * A plan feature this page belongs to. When the vendor's plan does not
   * include it, the page still renders, with the unlock card above it — the
   * same treatment as FeatureGate. The API refuses the actions themselves.
   */
  requiresFeature?: keyof PlanFeatures;
}

export const PageContainer = ({ children, title, breadcrumbs, hideTopNav, status, requiresFeature }: PageContainerProps) => {
  const autosave = useAutosaveReporterState();
  // A page nested in another page's tab has no bar of its own: its forms report
  // to the page that shows one.
  const parentReporter = useContext(AutosaveReporterContext);
  const ownsStatus = !hideTopNav || !parentReporter;
  const shownStatus = status ?? (autosave.status !== 'idle'
    ? <AutosaveIndicator status={autosave.status} onRetry={autosave.retry} />
    : undefined);

  const body = (
    <div
      className={cn(
        'pms-page relative mx-auto w-full max-w-[1600px] flex-1',
        !hideTopNav && 'px-4 py-6 sm:px-6 md:px-8',
      )}
      style={{ containerType: 'inline-size', containerName: PAGE_CONTAINER }}
    >
      {requiresFeature ? <FeatureGate feature={requiresFeature}>{children}</FeatureGate> : children}
    </div>
  );

  return (
    <div className={cn('relative flex flex-col bg-transparent', !hideTopNav && 'min-h-screen')}>
      {/*
       * The bar spans the window; the page below it does not.
       *
       * Chrome and content answer to different rules. Search, the assistant and
       * the account menu belong at the edges a person reaches for, while a
       * paragraph is unreadable at 2500px. Both used to sit inside one centred
       * column, which put the top bar's controls in the middle of a wide screen
       * with empty space either side of them.
       */}
      {!hideTopNav && <TopNavBar title={title} breadcrumbs={breadcrumbs} status={shownStatus} />}

      {/*
       * `pms-page` carries the MUI date-picker and carousel corrections in
       * index.css. They stay until the last MUI page is migrated.
       *
       * `container-type: inline-size` lets a layout ask how much room IT has
       * rather than how wide the window is — opening the assistant takes 460px
       * out of this column without the viewport moving. See PAGE_CONTAINER.
       */}
      {ownsStatus
        ? <AutosaveReporterContext.Provider value={autosave.reporter}>{body}</AutosaveReporterContext.Provider>
        : body}
    </div>
  );
};
